import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Bell, Check, CheckCheck, X, MessageCircle, RefreshCw, Truck, Store, Calendar, Eye } from 'lucide-react';
import { AdminLayout } from '@/components/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { formatPrice } from '@/lib/formatPrice';
import { isTodayRome, getMsUntilMidnightRome } from '@/lib/utils';
import { OrderDetailsModal } from '@/components/OrderDetailsModal';

// ✅ Base corretta (evita undefined)
const API_BASE = `${window.location.origin}/menu-digitale`;
const API = `${API_BASE}/api`;

// Formatter per data italiana
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('it-IT', {
    timeZone: 'Europe/Rome',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

const formatFullDate = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('it-IT', {
    timeZone: 'Europe/Rome',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

// Normalizza numero telefono per WhatsApp
const normalizePhone = (phone) => {
  if (!phone) return null;
  let cleaned = String(phone).replace(/[\s+\-.()\[\]]/g, '');
  if (cleaned.startsWith('00')) cleaned = cleaned.substring(2);
  if (cleaned.startsWith('0') && cleaned.length <= 11) cleaned = '39' + cleaned.substring(1);
  if (!cleaned.startsWith('39') && cleaned.length === 10) cleaned = '39' + cleaned;
  if (cleaned.startsWith('3') && cleaned.length === 10) cleaned = '39' + cleaned;
  return cleaned;
};

// Estrae ordine dal titolo tipo "Ordine #10 accettato"
const extractOrderIdFromTitle = (title) => {
  const m = String(title || '').match(/#(\d+)/);
  return m ? Number(m[1]) : null;
};

// Se in futuro salvi JSON nel message, lo legge (non obbligatorio)
const parseMessageJson = (message) => {
  try {
    const obj = JSON.parse(message);
    return obj && typeof obj === 'object' ? obj : null;
  } catch {
    return null;
  }
};

export default function AdminNotifications() {
  const { token } = useAuth();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('unread'); // 'all' | 'unread'
  const [showAllDays, setShowAllDays] = useState(false); // false = solo oggi

  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  const midnightTimeoutRef = useRef(null);

  // ✅ Cache ordini per evitare chiamate ripetute
  const ordersCacheRef = useRef(new Map()); // key: orderId -> orderData
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const getNotificationRead = (n) => {
    // backend: is_read (0/1) oppure read_at
    if (typeof n?.is_read !== 'undefined') return Number(n.is_read) === 1;
    if (typeof n?.read !== 'undefined') return !!n.read; // compat vecchio
    if (n?.read_at) return true;
    return false;
  };

  const enrichNotification = (n) => {
    const msgObj = parseMessageJson(n.message);
    const orderId = (msgObj && msgObj.order_id) ? Number(msgObj.order_id) : extractOrderIdFromTitle(n.title);

    return {
      ...n,
      id: Number(n.id),                 // ✅ IMPORTANTISSIMO
      admin_user_id: n.admin_user_id ? Number(n.admin_user_id) : n.admin_user_id,
      order_id: orderId ? Number(orderId) : null,
      read: getNotificationRead(n),
      is_read: (typeof n.is_read !== 'undefined') ? Number(n.is_read) : n.is_read,
    };
  };

  // ---- Fetch notifications ----
  const fetchNotifications = useCallback(async () => {
    if (!token) return;

    try {
      const todayParam = showAllDays ? 'false' : 'true';
      const res = await fetch(`${API}/admin/notifications?today_only=${todayParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Errore nel caricamento notifiche');

      const data = await res.json();
      const arr = Array.isArray(data) ? data : [];
      setNotifications(arr.map(enrichNotification));
    } catch (error) {
      toast.error(error?.message || 'Errore');
    } finally {
      setLoading(false);
    }
  }, [token, showAllDays]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // ---- Midnight reset (solo oggi) ----
  useEffect(() => {
    if (showAllDays) return;

    const scheduleMidnightReset = () => {
      const msUntilMidnight = getMsUntilMidnightRome();
      midnightTimeoutRef.current = setTimeout(() => {
        setNotifications([]);
        setLoading(true);
        fetchNotifications();
        scheduleMidnightReset();
      }, msUntilMidnight);
    };

    scheduleMidnightReset();

    return () => {
      if (midnightTimeoutRef.current) clearTimeout(midnightTimeoutRef.current);
    };
  }, [showAllDays, fetchNotifications]);

  // ✅ Carica ordine (usando admin/orders.php) e salva in cache
  const fetchOrderById = useCallback(async (orderId) => {
    const oid = Number(orderId);
    if (!oid) return null;
    if (ordersCacheRef.current.has(oid)) return ordersCacheRef.current.get(oid);
    if (!token) return null;

    const res = await fetch(`${API}/admin/orders`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) throw new Error('Errore caricamento ordini');

    const orders = await res.json();
    const found = (Array.isArray(orders) ? orders : []).find(o => Number(o.id) === oid);
    if (found) ordersCacheRef.current.set(oid, found);
    return found || null;
  }, [token]);

  // ---- Open/Close details ----
  const openDetails = async (orderId) => {
    const oid = Number(orderId);
    if (!oid) {
      toast.error('ID ordine non trovato nella notifica');
      return;
    }
    try {
      const order = await fetchOrderById(oid);
      if (!order) {
        toast.error('Ordine non trovato');
        return;
      }
      setSelectedOrder(order);
      setIsDetailsOpen(true);
    } catch (e) {
      toast.error(e?.message || 'Errore caricamento ordine');
    }
  };

  const closeDetails = () => {
    setIsDetailsOpen(false);
    setSelectedOrder(null);
  };

  const handleOrderCancelSuccess = (orderId) => {
    const oid = Number(orderId);

    setNotifications(prev =>
      prev.map(n => (Number(n.order_id) === oid ? { ...n, type: 'order_cancelled', title: `Ordine #${oid} annullato` } : n))
    );

    if (ordersCacheRef.current.has(oid)) {
      const o = ordersCacheRef.current.get(oid);
      ordersCacheRef.current.set(oid, { ...o, status: 'cancelled' });
    }
    closeDetails();
  };

  // ---- Mark as read ----
  const markAsRead = async (notificationId) => {
    const nid = Number(notificationId);
    try {
      const res = await fetch(`${API}/admin/notifications/${nid}/read`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id: nid, is_read: 1 })
      });
      if (!res.ok) throw new Error('Errore');

      setNotifications(prev =>
        prev.map(n => (Number(n.id) === nid ? { ...n, read: true, is_read: 1 } : n))
      );
    } catch {
      toast.error('Errore nel segnare come letta');
    }
  };

  // ---- Mark all as read ----
  const markAllAsRead = async () => {
    const unread = notifications.filter(n => !n.read);
    if (!unread.length) return;

    try {
      // usa endpoint dedicato
      const res = await fetch(`${API}/admin/notifications/mark-all-read?today_only=${showAllDays ? 'false' : 'true'}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Errore');

      setNotifications(prev => prev.map(n => ({ ...n, read: true, is_read: 1 })));
      toast.success('Tutte le notifiche segnate come lette');
    } catch {
      toast.error('Errore');
    }
  };

  // ---- Reject order (se non hai endpoint, lo togliamo o lo adeguiamo dopo) ----
  const handleRejectOrder = async () => {
    if (!rejectDialog) return;

    const orderId = rejectDialog.order_id || extractOrderIdFromTitle(rejectDialog.title);
    const oid = Number(orderId);
    if (!oid) {
      toast.error('ID ordine non trovato nella notifica');
      return;
    }

    setRejecting(true);
    try {
      // ⚠️ Se NON hai questo endpoint, qui va 404.
      // Se vuoi lo sistemiamo dopo lato PHP, per ora lascialo ma sappi che deve esistere.
      const res = await fetch(`${API}/admin/orders/${oid}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ reason: rejectReason || '' })
      });

      if (!res.ok) throw new Error("Errore nel rifiutare l'ordine");

      toast.success('Ordine rifiutato con successo');
      setRejectDialog(null);
      setRejectReason('');

      setNotifications(prev =>
        prev.map(n => (Number(n.id) === Number(rejectDialog.id) ? { ...n, type: 'order_cancelled', title: `Ordine #${oid} rifiutato` } : n))
      );
    } catch (error) {
      toast.error(error?.message || 'Errore');
    } finally {
      setRejecting(false);
    }
  };

  // ---- WhatsApp: usa i dati dell’ordine vero ----
  const openWhatsAppForOrder = async (notification) => {
    const orderId = notification.order_id || extractOrderIdFromTitle(notification.title);
    const oid = Number(orderId);
    if (!oid) {
      toast.error('ID ordine non trovato');
      return;
    }

    try {
      const order = await fetchOrderById(oid);
      if (!order) {
        toast.error('Ordine non trovato');
        return;
      }

      const normalizedPhone = normalizePhone(order.customer_phone);
      if (!normalizedPhone) {
        toast.error('Numero di telefono non disponibile');
        return;
      }

      const customerName = order.customer_name || 'cliente';
      const total = Number(order.total || 0);

      const message = encodeURIComponent(
        `Ciao ${customerName} 👋\n✅ Il tuo ordine #${oid} è stato preso in carico.\nTotale: ${formatPrice(total)}\nGrazie!`
      );

      window.open(`https://wa.me/${normalizedPhone}?text=${message}`, '_blank');
    } catch (e) {
      toast.error(e?.message || 'Errore WhatsApp');
    }
  };

  // ---- Filters ----
  const todayFilteredNotifications = showAllDays
    ? notifications
    : notifications.filter(n => isTodayRome(n.created_at));

  const filteredNotifications = useMemo(() => {
    const base = filter === 'unread'
      ? todayFilteredNotifications.filter(n => !n.read)
      : todayFilteredNotifications;
    return base;
  }, [todayFilteredNotifications, filter]);

  const unreadCount = todayFilteredNotifications.filter(n => !n.read).length;

  const todayFormatted = new Intl.DateTimeFormat('it-IT', {
    timeZone: 'Europe/Rome',
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  }).format(new Date());

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Bell className="w-6 h-6" />
              {showAllDays ? 'Tutte le Notifiche' : 'Notifiche di Oggi'}
              {unreadCount > 0 && (
                <Badge variant="destructive" className="ml-2">
                  {unreadCount} nuove
                </Badge>
              )}
            </h1>
            <p className="text-muted-foreground flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {todayFormatted}
            </p>
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <div className="flex items-center gap-2 mr-4">
              <Label htmlFor="show-all" className="text-sm text-muted-foreground">
                {showAllDays ? 'Tutte' : 'Solo oggi'}
              </Label>
              <Switch
                id="show-all"
                checked={showAllDays}
                onCheckedChange={(checked) => {
                  setShowAllDays(checked);
                  setLoading(true);
                }}
              />
            </div>

            <Button variant="outline" size="sm" onClick={fetchNotifications}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Aggiorna
            </Button>

            {unreadCount > 0 && (
              <Button variant="outline" size="sm" onClick={markAllAsRead}>
                <CheckCheck className="w-4 h-4 mr-2" />
                Segna tutte lette
              </Button>
            )}
          </div>
        </div>

        {/* Filter Tabs */}
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList>
            <TabsTrigger value="all">
              Tutte ({todayFilteredNotifications.length})
            </TabsTrigger>
            <TabsTrigger value="unread">
              Non lette ({unreadCount})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Notifications List */}
        <div className="space-y-3">
          {filteredNotifications.length === 0 ? (
            <Card className="p-8 text-center">
              <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {filter === 'unread'
                  ? 'Nessuna notifica non letta'
                  : showAllDays
                    ? 'Nessuna notifica'
                    : 'Nessuna notifica per oggi'}
              </p>
              {!showAllDays && (
                <Button variant="link" className="mt-2" onClick={() => setShowAllDays(true)}>
                  Mostra tutte le notifiche
                </Button>
              )}
            </Card>
          ) : (
            filteredNotifications.map((notification) => {
              const orderId = notification.order_id || extractOrderIdFromTitle(notification.title);

              return (
                <Card
                  key={notification.id}
                  className={`transition-all cursor-pointer hover:shadow-md ${
                    !notification.read ? 'border-l-4 border-l-orange-500 bg-orange-50/50 dark:bg-orange-950/20' : ''
                  } ${notification.type === 'order_cancelled' ? 'opacity-60' : ''}`}
                  onClick={() => openDetails(orderId)}
                >
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      {/* Left */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          {notification.delivery_type === 'PICKUP' ? (
                            <Badge variant="secondary" className="flex items-center gap-1">
                              <Store className="w-3 h-3" />
                              Ritiro
                            </Badge>
                          ) : notification.delivery_type === 'DELIVERY' ? (
                            <Badge variant="outline" className="flex items-center gap-1 border-blue-500 text-blue-600">
                              <Truck className="w-3 h-3" />
                              Consegna
                            </Badge>
                          ) : null}

                          {notification.type === 'order_cancelled' && (
                            <Badge variant="destructive">Annullato</Badge>
                          )}

                          {!notification.read && (
                            <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
                          )}

                          <span className="text-xs text-muted-foreground ml-auto">
                            {showAllDays ? formatFullDate(notification.created_at) : formatDate(notification.created_at)}
                          </span>
                        </div>

                        <h3 className="font-semibold text-foreground text-lg">{notification.title}</h3>
                      </div>

                      {/* Right Actions */}
                      <div className="flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-instagram-purple border-instagram-purple hover:bg-instagram-purple/10"
                          onClick={() => openDetails(orderId)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Dettagli
                        </Button>

                        {!notification.read && (
                          <Button variant="ghost" size="sm" onClick={() => markAsRead(notification.id)}>
                            <Check className="w-4 h-4 mr-1" />
                            Letta
                          </Button>
                        )}

                        <Button
                          variant="outline"
                          size="sm"
                          className="text-green-600 border-green-600 hover:bg-green-50"
                          onClick={() => openWhatsAppForOrder(notification)}
                        >
                          <MessageCircle className="w-4 h-4 mr-1" />
                          WhatsApp
                        </Button>

                        {notification.type !== 'order_cancelled' && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-destructive border-destructive hover:bg-destructive/10"
                            onClick={() => setRejectDialog(notification)}
                          >
                            <X className="w-4 h-4 mr-1" />
                            Rifiuta
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Reject Dialog */}
        <Dialog open={!!rejectDialog} onOpenChange={() => setRejectDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Rifiuta Ordine</DialogTitle>
              <DialogDescription>
                Stai per rifiutare l'ordine. Questa azione non può essere annullata.
              </DialogDescription>
            </DialogHeader>

            <div>
              <Label htmlFor="reject-reason">Motivo (opzionale)</Label>
              <Textarea
                id="reject-reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Es: Prodotto non disponibile, Chiusura anticipata..."
                className="mt-2"
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setRejectDialog(null)}>
                Annulla
              </Button>
              <Button variant="destructive" onClick={handleRejectOrder} disabled={rejecting}>
                {rejecting ? 'Rifiuto in corso...' : 'Conferma Rifiuto'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Order Details Modal */}
        <OrderDetailsModal
          orderId={selectedOrder?.id || null}
          orderData={selectedOrder || null} // ✅ niente /admin/orders/{id}
          open={isDetailsOpen}
          onClose={closeDetails}
          onCancelSuccess={handleOrderCancelSuccess}
          token={token}
        />
      </div>
    </AdminLayout>
  );
}